import { defaultApi, defaultClient, mockApi } from '@mint-sdk/api-core';
import { z } from 'zod';

import {
  BulkDeleteFolderRequestSchema,
  BulkUpdateFolderRequestSchema,
  BulkUpdateFolderRequestSchemaMdm,
  BulkUpdateFolderResponseSchema,
  CreateFolderRequestSchema,
  CreateFolderResponseSchema,
  DuplicateFolderRequestSchema,
  DuplicateFolderResponseSchema,
  GetPlacementFoldersRequestSchema,
  GetPlacementFoldersRequestSchemaMdm,
  GetPlacementFoldersResponseSchemaMdm,
  GetPlacementFoldersResponseSchemaOld,
  MdmOrderingSchema,
  MultiUpdatePlacementFoldersRequestSchema,
  MultiUpdatePlacementFoldersResponseSchema,
  PlacementFolderSchemaMdm,
  PlacementFolderSchemaOld,
} from '../shared/schemas/placementFolder.js';
import {
  PricingItemSchemaMdm,
  PricingItemSchemaOld,
} from '../shared/schemas/pricingItem.js';

export { MdmAttributeValueSchema } from '../shared/schemas/mdmAttribute.js';
export {
  PlacementFolderSchemaOld,
  PlacementFolderSchemaMdm,
} from '../shared/schemas/placementFolder.js';

export {
  PricingItemSchemaOld,
  PricingItemSchemaMdm,
} from '../shared/schemas/pricingItem.js';

export type GetPlacementFoldersPayload = z.infer<
  typeof GetPlacementFoldersRequestSchema
>;
export type PlacementFoldersGridMdmOrdering = z.infer<typeof MdmOrderingSchema>;
export type PlacementFolderMdm = z.infer<typeof PlacementFolderSchemaMdm>;
export type PricingItemMdm = z.infer<typeof PricingItemSchemaMdm>;

// TODO(ghostMdm): remove with `ff.ghostMdm.enabled`
export type PlacementFolderOld = z.infer<typeof PlacementFolderSchemaOld>;
export type PricingItemOld = z.infer<typeof PricingItemSchemaOld>;
export type BulkUpdatePlacementFolderPayload = z.infer<
  typeof BulkUpdateFolderRequestSchema
>;
export type BulkUpdatePlacementFolderMdmPayload = z.infer<
  typeof BulkUpdateFolderRequestSchemaMdm
>;
export type BulkDeletePlacementFolderPayload = z.infer<
  typeof BulkDeleteFolderRequestSchema
>;
export type MultiUpdatePlacementFoldersPayload = z.infer<
  typeof MultiUpdatePlacementFoldersRequestSchema
>;
export type MultiUpdatePlacementFoldersResponse = z.infer<
  typeof MultiUpdatePlacementFoldersResponseSchema
>;

export const getPlacementFolders = defaultApi.declareEndpoint({
  url: 'planning-service/virtuals/placement-folders/grid/',
  method: 'POST',
  requestSchema: GetPlacementFoldersRequestSchema,
  responseSchema: GetPlacementFoldersResponseSchemaOld,
});

// TODO(ghostMdm): remove legacy `grid/` endpoint with `ff.ghostMdm.enabled`.
export const getPlacementFoldersQueryOptions = defaultClient.query({
  url: 'planning-service/virtuals/placement-folders/grid/',
  method: 'POST',
  requestSchema: GetPlacementFoldersRequestSchema,
  responseSchema: GetPlacementFoldersResponseSchemaOld,
  querySchema: z.object({ parentId: z.string() }),
  processResponse(data) {
    return data;
  },
});

export const placementFoldersGridMdmEndpoint = defaultApi.declareEndpoint({
  url: 'planning-service/virtuals/placement-folders/grid-v2/',
  method: 'POST',
  requestSchema: GetPlacementFoldersRequestSchemaMdm,
  responseSchema: GetPlacementFoldersResponseSchemaMdm,
});

export const createPlacementFolder = defaultClient.mutation({
  url: 'planning-service/virtuals/placement-folders/bulk-create/',
  method: 'POST',
  requestSchema: CreateFolderRequestSchema,
  responseSchema: CreateFolderResponseSchema,
  useKey: true,
  processResponse(data) {
    return data;
  },
});

export const createPlacementFolderMdm = defaultClient.mutation({
  url: 'planning-service/virtuals/placement-folders/bulk-create-v2/',
  method: 'POST',
  requestSchema: CreateFolderRequestSchema,
  responseSchema: CreateFolderResponseSchema,
  useKey: true,
  processResponse(data) {
    return data;
  },
});

export const bulkUpdatePlacementFolder = defaultClient.mutation({
  url: 'planning-service/virtuals/placement-folders/bulk-update/',
  method: 'POST',
  requestSchema: BulkUpdateFolderRequestSchema,
  responseSchema: BulkUpdateFolderResponseSchema,
  useKey: true,
  processResponse(data) {
    return data;
  },
});

export const bulkUpdatePlacementFolderMdm = defaultClient.mutation({
  url: 'planning-service/virtuals/placement-folders/bulk-update-v2/',
  method: 'POST',
  requestSchema: BulkUpdateFolderRequestSchemaMdm,
  responseSchema: BulkUpdateFolderResponseSchema,
  useKey: true,
  processResponse(data) {
    return data;
  },
});

export const duplicatePlacementFolder = defaultClient.mutation({
  url: 'planning-service/virtuals/placement-folders/duplicate/',
  method: 'POST',
  requestSchema: DuplicateFolderRequestSchema,
  responseSchema: DuplicateFolderResponseSchema,
  useKey: true,
  processResponse(data) {
    return data;
  },
});

export const duplicatePlacementFolderMdm = defaultClient.mutation({
  url: 'planning-service/virtuals/placement-folders/duplicate-v2/',
  method: 'POST',
  requestSchema: DuplicateFolderRequestSchema,
  responseSchema: DuplicateFolderResponseSchema,
  useKey: true,
  processResponse(data) {
    return data;
  },
});

export const bulkDeletePlacementFolder = defaultClient.mutation({
  url: 'planning-service/virtuals/placement-folders/bulk-delete/',
  method: 'POST',
  requestSchema: BulkDeleteFolderRequestSchema,
  responseSchema: z.string(),
  useKey: true,
  processResponse(data) {
    return data;
  },
});

export const multiUpdatePlacementFolders = defaultClient.mutation({
  url: 'planning-service/virtuals/placement-folders/multi-update/',
  method: 'POST',
  requestSchema: MultiUpdatePlacementFoldersRequestSchema,
  responseSchema: MultiUpdatePlacementFoldersResponseSchema,
  useKey: true,
  processResponse(data) {
    return data;
  },
});

export const exportPlacementFolders = mockApi.declareStream({
  url: 'planning-service/virtuals/placement-folders/export/' as const,
  method: 'GET',
  querySchema: z.object({ virtualDspCampaignId: z.string() }),
  clientOptions: {
    request: {
      snakeCaseQueryParams: true,
    },
  },
  fullResponse: true,
  mock: ({ params }) => {
    const blob = new Blob(['hello'], { type: 'text/plain' });
    const fileName =
      typeof params?.virtualDspCampaignId === 'string'
        ? `virtual_settings_${params.virtualDspCampaignId}.txt`
        : 'virtual_settings.txt';

    return {
      data: blob,
      status: 200,
      statusText: 'OK',
      headers: {
        'content-disposition': `attachment; filename="${fileName}"`,
      },
    };
  },
});
